import styles from './WorkerAssignmentBox.module.scss';
import { Driver, Order, Vehicle } from 'types';
import { Draggable, Droppable } from 'react-beautiful-dnd';
import CalendarTicket from './components/CalendarTicket';
import { getOrderDeliveryTimePreference } from 'utils/order-helpers';
import Typography from 'components/Typography';
import IconUsers from 'pages/DeliveryPlanner/icons/users';
import IconUser from 'pages/DeliveryPlanner/icons/user';
import classNames from 'classnames';
import { PinIcon } from '../OrderDetailsModal/icons';
import { Route } from 'pages/DeliveryPlanner/useDeliveryPlanner';
import { kmToMiles } from 'helpers/kmToMiles';
import { Fragment } from 'react/jsx-runtime';

export type TimeType = {
  time: number;
  addition: string;
  icon: React.ReactNode;
  route: Route;
};

type Props = {
  teams: Vehicle[];
  orders: Order[];
  onOrdersClick: (
    order: Order
  ) => (e: React.MouseEvent<HTMLDivElement>) => void;
  showEstTime: boolean;
  selectedWorker: Driver | Vehicle;
  route: Route;
  rightItems: React.ReactNode[];
  invalidOrderIds: number[];
  hidePlaceholder?: boolean;
};

function WorkerAssignmentBox({
  orders,
  invalidOrderIds,
  onOrdersClick,
  showEstTime,
  selectedWorker,
  route,
  rightItems,
  hidePlaceholder,
}: Props) {
  const total_time = route?.total_time
    ? `${Math.floor(route.total_time / 60)}h ${route.total_time % 60}m`
    : '0:00';
  const total_distance = kmToMiles(route.total_distance || 0);
  const isIndividual = !!selectedWorker && 'driver_id' in selectedWorker;
  const assignedWorkerOrTeam = isIndividual
    ? (selectedWorker as Driver)
    : (selectedWorker as Vehicle);
  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <div
          className={classNames(
            styles.selectedWorkerContainer,
            isIndividual && styles.individual
          )}
        >
          {isIndividual ? (
            <IconUser width={20} height={20} />
          ) : (
            <IconUsers width={20} height={20} />
          )}
          <div className={styles.column}>
            <Typography variant="h3" className={styles.name}>
              {isIndividual
                ? (assignedWorkerOrTeam as Driver)?.first_name +
                ' ' +
                (assignedWorkerOrTeam as Driver)?.last_name
                : (assignedWorkerOrTeam as Vehicle)?.name}
            </Typography>
            <div className={styles.info}>
              <span>{route.deliveries.length} stops</span>
              <span>•</span>
              <span>{total_time}</span>
              <span>•</span>
              <span>{total_distance} mi</span>
            </div>
          </div>
        </div>
        {rightItems.map((item, index) => (
          <Fragment key={index}>{item}</Fragment>
        ))}
      </div>
      <Droppable droppableId={'assigned_list'} direction="horizontal">
        {(provider, snapshot) => (
          <div
            ref={provider.innerRef}
            className={classNames(
              styles.ordersRoot,
              snapshot.isDraggingOver && styles.isDraggingOver
            )}
            {...provider.droppableProps}
          >
            <div className={classNames(styles.orders)}>
              {orders.map((order, index) => {
                return (
                  <Draggable
                    draggableId={`${order.id}`}
                    index={index}
                    key={order.id}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        className={classNames(
                          snapshot.isDragging && styles.isDragging
                        )}
                      >
                        <CalendarTicket
                          key={order.id}
                          {...order}
                          index={index + 1}
                          timePreference={getOrderDeliveryTimePreference(order)}
                          onOrdersClick={onOrdersClick(order)}
                          showEstTime={showEstTime}
                          invalid={invalidOrderIds.includes(
                            order.orders[0].sequential_order_number ?? 0
                          )}
                        />
                      </div>
                    )}
                  </Draggable>
                );
              })}
              {orders.length === 0 && (
                <div className={styles.empty}>
                  <PinIcon width={24} height={24} />
                  <Typography variant="label">
                    Drop tickets here to assign
                  </Typography>
                </div>
              )}
              {orders.length > 0 && !hidePlaceholder && (
                <div className={styles.guideText}>
                  <span>Drag & Drop to rearrange the stops.</span>
                  <span>
                    When ready, click route jobs to see route on the map
                  </span>
                </div>
              )}
            </div>

            {provider.placeholder}
          </div>
        )}
      </Droppable>
    </div>
  );
}

export default WorkerAssignmentBox;
